import React from 'react'
import ListenButton from '@/components/atoms/ListenButton/ListenButton'
import clsx from 'clsx'
import * as styles from '@/components/organisms/Learning/LearningSytle.css'
import { Icon } from '@/icons'
import { motion } from 'framer-motion'
import GradingState from '@/components/organisms/Grading/GradingState'

interface TaskCardProps {
  hasImage?: boolean
  hasListenButton?: boolean
  questionType: 'multiple' | 'short' | 'essay'
  question: string
  options?: Array<{ id: number; word: string }>
  className?: string
  showFeedback?: boolean
  showAnswer?: boolean
  isExamMode?: boolean
  imageUrl?: string | null
  audioUrl?: string | null
  selectedOptionIds?: number[]
  onToggleOption?: (id: number) => void
  valueText?: string
  onChangeText?: (value: string) => void
  readOnly?: boolean
}

export default function TaskCard({ 
    className,
    hasImage = true, 
    hasListenButton = true, 
    questionType, 
    question, 
    options = [],
    showFeedback = false,
    isExamMode = false,
    imageUrl = null,
    audioUrl = null,
    selectedOptionIds = [],
    onToggleOption,
    valueText = '',
    onChangeText,
    readOnly = false,
}: TaskCardProps) {
    // 임의 데이터
    const taskData = [
        { id: 1, word: 'apple' },
        { id: 2, word: 'banana' },
        { id: 3, word: 'orange' },
        { id: 4, word: 'grape' },
        { id: 5, word: 'strawberry' },
    ];
    
  return (
    <div className={className}>
        <h1 className='pb-6 text-lg font-bold whitespace-pre-wrap'>{question}</h1>
        {/* 이미지가 없더라도 오디오가 있으면 별도 버튼 노출 */}
        {!hasImage && hasListenButton && audioUrl && (
            <div className='pb-3'>
                <ListenButton size={36} audioSrc={audioUrl || undefined} />
            </div>
        )}
        
        {/* 이미지 섹션 */}
        {hasImage && imageUrl && (
            <div className='flex gap-2 justify-center items-center pb-5'>
                <div className='relative w-full flex justify-center'>
                    <img 
                        src={imageUrl} 
                        alt="question" 
                        className='object-contain overflow-hidden rounded-2xl border border-gray-200 max-w-full max-h-[600px] w-auto h-auto' 
                    />
                    {hasListenButton && <ListenButton size={40} className='absolute -top-4 -right-4' audioSrc={audioUrl || undefined} />}
                </div>
            </div>
        )}
        
        <section>
            {/* 객관식 */}
            {questionType === 'multiple' && (
                <ul className='flex flex-wrap gap-x-10 gap-y-4 items-start'>
                    {options.map((item) => {
                        const isSelected = selectedOptionIds?.includes(item.id);
                        return (
                        <li 
                            key={item.id}
                            className={clsx(readOnly ? 'cursor-default select-none' : 'cursor-pointer')}
                            onClick={readOnly ? undefined : () => onToggleOption && onToggleOption(item.id)}
                        >
                            <div className='flex items-center gap-3'>
                                <span
                                    className={`relative flex items-center justify-center size-8 rounded-full border text-sm font-semibold transition-all ${
                                        isSelected ? 'border-gray-300 text-gray-900 ring-2 ring-red-400 ring-offset-2 ring-offset-white' : 'border-gray-300 text-gray-700'
                                    }`}
                                >
                                    {item.id}
                                </span>
                                <span className={`font-bold ${isSelected ? 'text-gray-900' : 'text-gray-800'}`}>{item.word}</span>
                            </div>
                        </li>
                        );
                    })}
                </ul>
            )}

            {/* 주관식 */}
            {questionType === 'short' && (
                <div className='pb-0'>
                    {readOnly ? (
                        <div
                            className={clsx(styles.graInput, '!text-base !py-2.5 !border-2 cursor-default select-none text-gray-400')}
                            aria-hidden="true"
                        >
                            {valueText || '정답을 입력해주세요.'}
                        </div>
                    ) : (
                        <input
                            placeholder="정답을 입력해주세요."
                            type="text"
                            className={clsx(styles.graInput, '!text-base !py-2.5 !border-2')}
                            value={valueText}
                            onChange={(e)=> onChangeText && onChangeText(e.target.value)}
                        />
                    )}
                </div>
            )}

            {/* 서술형 */}
            {questionType === 'essay' && (
                <div className='pb-0'>
                    {readOnly ? (
                        <div
                            className={clsx(styles.graInput, '!text-base !py-2.5 !border-2 !resize-none min-h-[160px] cursor-default select-none text-gray-400')}
                            aria-hidden="true"
                        >
                            {valueText || '정답을 입력해주세요.'}
                        </div>
                    ) : (
                        <textarea
                            rows={5}
                            placeholder="정답을 입력해주세요."
                            className={clsx(styles.graInput, '!text-base !py-2.5 !border-2 !resize-none')}
                            maxLength={1000}
                            value={valueText}
                            onChange={(e)=> onChangeText && onChangeText(e.target.value)}
                        />
                    )}
                </div>
            )}
        </section>
        
        {(questionType === 'short' || questionType === 'essay') && !isExamMode && (
        <section className='mt-5'>
            <div className='overflow-hidden rounded-2xl border border-red-400'>
                <div className='flex justify-between items-center px-4 py-2 w-full'>
                    <p className='text-sm font-bold text-gray-500'>입력한 답</p>
                    <GradingState status={'incorrect'} />
                </div>
                <p className='flex justify-center items-center py-3 bg-gray-50'>
                    <span className='text-2xl font-bold'>
                        I like winter
                    </span>
                </p>
            </div>
        </section>
        )}


        {/* 채점 상태, 오답 노트일때 노출 되는 영역 */}
        {showFeedback && (
        <section className='mt-5'>
        <motion.div
            key="feedback-inner"
            className="px-4 py-3 text-blue-500 bg-gray-100 rounded-2xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
        >
            <div className="flex gap-3 items-center">
            <p className="text-sm">정답</p>
            <span className="flex justify-center items-center w-4 h-4 text-xs rounded-full border border-blue-500">
                1
            </span>
            </div>
            <p className="pt-1 text-sm">
            관리자에서 설정한 정답사유가 추가로 출력되는 영역
            </p>
        </motion.div>
        </section>
        )}
    </div>
  )
}